import Badge from '@mui/icons-material/Badge'
import Cancel from '@mui/icons-material/Cancel'
import CheckCircle from '@mui/icons-material/CheckCircle'
import ContentCopy from '@mui/icons-material/ContentCopy'
import Edit from '@mui/icons-material/Edit'
import ExpandMore from '@mui/icons-material/ExpandMore'
import Lock from '@mui/icons-material/Lock'
import Star from '@mui/icons-material/Star'
import StarBorder from '@mui/icons-material/StarBorder'
import SupervisorAccount from '@mui/icons-material/SupervisorAccount'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  FormControlLabel,
  IconButton,
  Stack,
  Switch,
  Tooltip,
  Typography,
} from '@mui/material'
import { useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import {
  getDataMapFromCommonResponse,
  getImageLink,
  isCommonResponseSuccessfulV2,
} from 'src/mentor/helpers/utilityFunctions'
import {
  approveAssignment,
  updateAssignmentDefault,
  updateAssignmentEnabled,
  updateAssignmentPermissions,
  updateAssignmentSupportRoles,
} from 'src/supportPortal/services/clinicAssistantAdminService'
import SupportRoleEditor from './SupportRoleEditor'

// Editable via PATCH /assignment/permissions. The two `readOnly` flags live on the entity but
// are not part of the permissions DTO, so we surface them as read-only for transparency.
const PERMISSION_FIELDS = [
  { key: 'canViewPatientHistory', label: 'View patient history' },
  { key: 'canViewJoiningDetails', label: 'View joining details' },
  { key: 'canViewTreatmentHistory', label: 'View treatment history', readOnly: true },
  { key: 'canViewPatientHistoryNotes', label: 'View patient history notes', readOnly: true },
  { key: 'canMarkEventsAsCompleted', label: 'Mark events as completed' },
  { key: 'canRescheduleBookings', label: 'Reschedule bookings' },
  { key: 'canViewPrescriptions', label: 'View prescriptions' },
  { key: 'canDownloadPrescription', label: 'Download prescription' },
  { key: 'canViewTherapyReports', label: 'View therapy reports' },
  { key: 'canViewScreeningReports', label: 'View screening reports' },
  { key: 'accessPatientDetails', label: 'Access patient details' },
  { key: 'accessPatientContact', label: 'Access patient contact' },
  { key: 'forceAnonymousPatientCreation', label: 'Force anonymous patient creation' },
]

const VOICE_FIELDS = [
  { key: 'callPatientEnabled', label: 'Call patient' },
  { key: 'callSupportTeamEnabled', label: 'Call support team' },
  { key: 'callRosteredDoctorEnabled', label: 'Call rostered doctor' },
  { key: 'callRosteredTherapistEnabled', label: 'Call rostered therapist' },
  { key: 'callRosteredScreeningMentorEnabled', label: 'Call rostered screening mentor' },
]

function fmtDate(value) {
  if (!value) return '—'
  if (typeof value === 'number') return new Date(value).toLocaleString()
  const s = String(value)
  return s.length > 19 ? s.slice(0, 19).replace('T', ' ') : s
}

function displayName(row) {
  const u = row?.user || {}
  const p = row?.profile || {}
  const full = `${u.firstName || ''} ${u.lastName || ''}`.trim()
  return p.name || full || u.userName || u.userId || 'Unknown assistant'
}

function initials(name) {
  const parts = String(name || '?')
    .trim()
    .split(/\s+/)
  return ((parts[0]?.[0] || '') + (parts[1]?.[0] || '')).toUpperCase() || '?'
}

function managerName(manager) {
  if (!manager) return 'Unknown manager'
  const full = `${manager.firstName || ''} ${manager.lastName || ''}`.trim()
  return full || manager.userName || manager.email || manager.managerId || 'Unknown manager'
}

// Practitioners (TCH- ids) referenced as a support role's manager are resolved from the page's
// allMentors list, which already carries name, profile pic, and servicesApproved.
function mentorDisplayName(mentor) {
  if (!mentor) return ''
  return mentor.name || `${mentor.firstName || ''} ${mentor.lastName || ''}`.trim() || ''
}

const SERVICE_ROLE_LABELS = {
  ROLE_DOCTOR: 'Doctor',
  ROLE_THERAPIST: 'Therapist',
  ROLE_SCREENER: 'Screener',
  ROLE_PSYCHIATRIST: 'Psychiatrist',
  ROLE_PSYCHOLOGIST: 'Psychologist',
}

function serviceLabel(key) {
  if (SERVICE_ROLE_LABELS[key]) return SERVICE_ROLE_LABELS[key]
  return String(key || '')
    .replace(/^ROLE_/, '')
    .replace(/_/g, ' ')
    .toLowerCase()
    .replace(/\b\w/g, (c) => c.toUpperCase())
}

function copy(value, label) {
  if (!value) return
  try {
    navigator.clipboard.writeText(String(value))
    toast.success(`${label || 'Value'} copied`)
  } catch (e) {
    toast.error('Copy failed')
  }
}

function isOk(resp) {
  return resp?.status === 200 && isCommonResponseSuccessfulV2(resp)
}

function SectionTitle({ icon, children, action }) {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        mb: 1,
        mt: 0.5,
      }}
    >
      <Stack direction="row" spacing={0.75} alignItems="center">
        {icon}
        <Typography variant="subtitle2" fontWeight={700} sx={{ color: '#334155' }}>
          {children}
        </Typography>
      </Stack>
      {action}
    </Box>
  )
}

// label + monospace value + copy button
function CopyField({ label, value }) {
  if (!value) return null
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
      <Typography variant="caption" sx={{ color: 'text.secondary', flexShrink: 0 }}>
        {label}:
      </Typography>
      <Typography
        variant="caption"
        sx={{ fontFamily: 'monospace', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis' }}
        noWrap
      >
        {value}
      </Typography>
      <Tooltip title={`Copy ${label}`}>
        <IconButton size="small" sx={{ p: 0.25 }} onClick={() => copy(value, label)}>
          <ContentCopy sx={{ fontSize: 13 }} />
        </IconButton>
      </Tooltip>
    </Box>
  )
}

function KeyVal({ label, value }) {
  return (
    <Box sx={{ minWidth: 0 }}>
      <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
        {label}
      </Typography>
      <Typography variant="body2" sx={{ fontWeight: 500, wordBreak: 'break-word' }}>
        {value || '—'}
      </Typography>
    </Box>
  )
}

export default function ClinicAssistantCard({
  row,
  brandNameById = {},
  availableManagers = [],
  mentorById = {},
  canManageLifecycle = false,
  onReload,
}) {
  // Local optimistic copy of the assignment; resynced whenever the parent reloads.
  const [assignment, setAssignment] = useState(row?.assignment || {})
  const [savingKey, setSavingKey] = useState(null)
  const [lifecycleBusy, setLifecycleBusy] = useState(false)
  const [editorOpen, setEditorOpen] = useState(false)

  useEffect(() => {
    setAssignment(row?.assignment || {})
  }, [row])

  const user = row?.user || {}
  const profile = row?.profile || {}
  const managers = row?.managers || []
  const supportRoles = assignment?.supportRoles || []
  const voice = assignment?.portalVoiceCallPermissions || {}
  const name = displayName(row)
  const isDefault = Boolean(assignment?.defaultForClinicLocation)
  const isEnabled = assignment?.enabled !== false
  const kyc = (assignment?.kycStatus || 'pending').toLowerCase()

  async function patchPermission(key, value) {
    const prev = assignment
    setAssignment({ ...assignment, [key]: value })
    setSavingKey(key)
    const resp = await updateAssignmentPermissions({ assignmentId: assignment.id, [key]: value })
    setSavingKey(null)
    if (isOk(resp)) {
      toast.success('Permission updated')
    } else {
      setAssignment(prev)
      toast.error(resp?.response?.data?.message || 'Update failed')
    }
  }

  async function patchVoice(key, value) {
    const prev = assignment
    const nextVoice = { ...voice, [key]: value }
    setAssignment({ ...assignment, portalVoiceCallPermissions: nextVoice })
    setSavingKey(`voice:${key}`)
    const resp = await updateAssignmentPermissions({
      assignmentId: assignment.id,
      portalVoiceCallPermissions: nextVoice,
    })
    setSavingKey(null)
    if (isOk(resp)) {
      toast.success('Voice-call permission updated')
    } else {
      setAssignment(prev)
      toast.error(resp?.response?.data?.message || 'Update failed')
    }
  }

  async function toggleDefault() {
    setLifecycleBusy(true)
    const resp = await updateAssignmentDefault({
      assignmentId: assignment.id,
      defaultForClinicLocation: !isDefault,
    })
    setLifecycleBusy(false)
    if (isOk(resp)) {
      toast.success(isDefault ? 'Unset as default' : 'Set as default for this location')
      onReload?.()
    } else {
      toast.error(resp?.response?.data?.message || 'Update failed')
    }
  }

  async function toggleEnabled() {
    setLifecycleBusy(true)
    const resp = await updateAssignmentEnabled({
      assignmentId: assignment.id,
      enabled: !isEnabled,
    })
    setLifecycleBusy(false)
    if (isOk(resp)) {
      toast.success(isEnabled ? 'Assignment disabled' : 'Assignment enabled')
      onReload?.()
    } else {
      toast.error(resp?.response?.data?.message || 'Update failed')
    }
  }

  async function doApproval(action) {
    setLifecycleBusy(true)
    const resp = await approveAssignment({
      assignmentId: assignment.id,
      clinicBrandId: assignment.clinicBrandId,
      action,
    })
    setLifecycleBusy(false)
    if (isOk(resp)) {
      toast.success(action === 'APPROVE' ? 'Assignment approved' : 'Assignment rejected')
      onReload?.()
    } else {
      toast.error(resp?.response?.data?.message || 'Action failed')
    }
  }

  async function saveSupportRoles(nextRoles) {
    const resp = await updateAssignmentSupportRoles({
      assignmentId: assignment.id,
      supportRoles: nextRoles,
    })
    if (isOk(resp)) {
      toast.success('Support roles updated')
      setEditorOpen(false)
      onReload?.()
      return true
    }
    toast.error(resp?.response?.data?.message || 'Could not update support roles')
    return false
  }

  return (
    <Box
      sx={{
        border: '1px solid #e2e8f0',
        borderRadius: 2,
        bgcolor: '#fff',
        boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
        opacity: isEnabled ? 1 : 0.78,
      }}
    >
      <Accordion disableGutters elevation={0} sx={{ '&:before': { display: 'none' }, bgcolor: 'transparent' }}>
        <AccordionSummary expandIcon={<ExpandMore />} sx={{ px: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, width: '100%', minWidth: 0 }}>
            <Avatar sx={{ bgcolor: isDefault ? '#7450aa' : '#94a3b8', width: 40, height: 40, fontSize: 15 }}>
              {initials(name)}
            </Avatar>
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Stack direction="row" spacing={0.75} alignItems="center" sx={{ minWidth: 0 }}>
                <Typography variant="subtitle1" fontWeight={700} noWrap>
                  {name}
                </Typography>
                {isDefault && (
                  <Tooltip title="Default assistant for this location">
                    <Star sx={{ fontSize: 18, color: '#f59e0b' }} />
                  </Tooltip>
                )}
              </Stack>
              <Typography variant="caption" sx={{ color: 'text.secondary' }} noWrap>
                {user.userName ? `@${user.userName}` : 'no username'}
                {user.email ? ` · ${user.email}` : ''}
              </Typography>
            </Box>
            <Stack direction="row" spacing={0.5} sx={{ flexShrink: 0, mr: 1 }}>
              <Chip
                size="small"
                label={isEnabled ? 'Enabled' : 'Disabled'}
                color={isEnabled ? 'success' : 'default'}
                variant={isEnabled ? 'filled' : 'outlined'}
                sx={{ height: 22 }}
              />
              <Chip
                size="small"
                label={`KYC: ${kyc}`}
                color={kyc === 'approved' ? 'success' : kyc === 'rejected' ? 'error' : 'warning'}
                variant="outlined"
                sx={{ height: 22 }}
              />
            </Stack>
          </Box>
        </AccordionSummary>
        <AccordionDetails sx={{ px: 2, pb: 2, pt: 0 }}>
          <Divider sx={{ mb: 1.5 }} />

          {/* Identity */}
          <SectionTitle icon={<Badge sx={{ fontSize: 18, color: '#7450aa' }} />}>Identity</SectionTitle>
          <Stack spacing={0.5} sx={{ mb: 1 }}>
            <CopyField label="User ID" value={user.userId || assignment.userId} />
            <CopyField label="Username" value={user.userName} />
            <CopyField label="Email" value={user.email || assignment.userEmailId} />
            <CopyField label="Mobile" value={user.mobile || assignment.assignmentMobile} />
          </Stack>
          <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
            {user.locked && (
              <Chip size="small" icon={<Lock sx={{ fontSize: 14 }} />} label="Account locked" color="error" variant="outlined" />
            )}
            {user.enabled === false && (
              <Chip size="small" label="Account disabled" color="error" variant="outlined" />
            )}
            {(user.authorities || []).map((role) => (
              <Chip key={role} size="small" label={role.replace('ROLE_', '')} variant="outlined" sx={{ height: 22 }} />
            ))}
          </Stack>

          <Divider sx={{ my: 1.5 }} />

          {/* Permissions */}
          <SectionTitle icon={<CheckCircle sx={{ fontSize: 18, color: '#10b981' }} />}>
            Permissions
          </SectionTitle>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 0.25 }}>
            {PERMISSION_FIELDS.map((field) => {
              const checked = Boolean(assignment?.[field.key])
              return (
                <FormControlLabel
                  key={field.key}
                  sx={{ m: 0 }}
                  control={
                    <Switch
                      size="small"
                      checked={checked}
                      disabled={field.readOnly || savingKey === field.key}
                      onChange={(e) => patchPermission(field.key, e.target.checked)}
                    />
                  }
                  label={
                    <Typography variant="caption" sx={{ color: field.readOnly ? 'text.disabled' : 'text.primary' }}>
                      {field.label}
                      {field.readOnly ? ' (read-only)' : ''}
                    </Typography>
                  }
                />
              )
            })}
          </Box>

          <Divider sx={{ my: 1.5 }} />

          {/* Voice-call permissions */}
          <SectionTitle icon={<CheckCircle sx={{ fontSize: 18, color: '#3b82f6' }} />}>
            Portal voice-call permissions
          </SectionTitle>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 0.25 }}>
            {VOICE_FIELDS.map((field) => (
              <FormControlLabel
                key={field.key}
                sx={{ m: 0 }}
                control={
                  <Switch
                    size="small"
                    checked={Boolean(voice?.[field.key])}
                    disabled={savingKey === `voice:${field.key}`}
                    onChange={(e) => patchVoice(field.key, e.target.checked)}
                  />
                }
                label={<Typography variant="caption">{field.label}</Typography>}
              />
            ))}
          </Box>

          <Divider sx={{ my: 1.5 }} />

          {/* Managers & support roles */}
          <SectionTitle
            icon={<SupervisorAccount sx={{ fontSize: 18, color: '#7450aa' }} />}
            action={
              <Button size="small" startIcon={<Edit sx={{ fontSize: 16 }} />} onClick={() => setEditorOpen(true)}>
                Edit
              </Button>
            }
          >
            Managers & support roles
          </SectionTitle>
          {supportRoles.length === 0 ? (
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              No managers or support roles assigned.
            </Typography>
          ) : (
            <Stack spacing={1}>
              {supportRoles.map((role, idx) => {
                const manager = managers[idx]
                const mentor = role.managerId ? mentorById[role.managerId] : null
                const resolvedName = mentorDisplayName(mentor) || managerName(manager)
                const services = Object.entries(mentor?.teacherUnEditableFields?.servicesApproved || {})
                return (
                  <Box
                    key={`${role.managerId || 'mgr'}-${idx}`}
                    sx={{ border: '1px solid #eef2f6', borderRadius: 1.5, p: 1.25, bgcolor: '#fafbfc' }}
                  >
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                      <Avatar
                        src={mentor?.profilePicId ? getImageLink(mentor.profilePicId) : undefined}
                        sx={{ width: 28, height: 28, fontSize: 12, bgcolor: '#cbd5e1' }}
                      >
                        {initials(resolvedName)}
                      </Avatar>
                      <Typography variant="body2" fontWeight={700}>
                        {resolvedName}
                      </Typography>
                      <Chip size="small" label={role.roleId || 'role'} sx={{ height: 20 }} />
                    </Stack>
                    <Stack spacing={0.25} sx={{ mb: 0.5 }}>
                      <CopyField label="Practitioner ID" value={role.managerId} />
                      <CopyField label="Email" value={mentor?.email || manager?.email || role.managerEmailId} />
                      {manager?.userName ? <CopyField label="Username" value={manager.userName} /> : null}
                    </Stack>
                    {services.length > 0 && (
                      <Box sx={{ mb: 0.5 }}>
                        <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 0.25 }}>
                          Approved services
                        </Typography>
                        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                          {services.map(([key, val]) => {
                            const on = Boolean(val?.enabled)
                            return (
                              <Chip
                                key={key}
                                size="small"
                                icon={on ? <CheckCircle sx={{ fontSize: 13 }} /> : <Cancel sx={{ fontSize: 13 }} />}
                                label={on ? serviceLabel(key) : `${serviceLabel(key)} · disabled`}
                                color={on ? 'success' : 'default'}
                                variant="outlined"
                                sx={{ height: 20, opacity: on ? 1 : 0.55 }}
                              />
                            )
                          })}
                        </Stack>
                      </Box>
                    )}
                    {(role.allowedUploadTypes?.length || role.allowedPrintTypes?.length || role.managedEvents?.length) ? (
                      <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mb: 0.5 }}>
                        {(role.allowedUploadTypes || []).map((t) => (
                          <Chip key={`u-${t}`} size="small" variant="outlined" color="primary" label={`upload: ${t}`} sx={{ height: 20 }} />
                        ))}
                        {(role.allowedPrintTypes || []).map((t) => (
                          <Chip key={`p-${t}`} size="small" variant="outlined" color="secondary" label={`print: ${t}`} sx={{ height: 20 }} />
                        ))}
                        {(role.managedEvents || []).map((t) => (
                          <Chip key={`e-${t}`} size="small" variant="outlined" label={`event: ${t}`} sx={{ height: 20 }} />
                        ))}
                      </Stack>
                    ) : null}
                    <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                      {[
                        ['Patient details', role.accessPatientDetails],
                        ['Patient contact', role.accessPatientContact],
                        ['History notes', role.accessPatientHistoryNotes],
                      ].map(([lbl, val]) => (
                        <Chip
                          key={lbl}
                          size="small"
                          icon={val ? <CheckCircle sx={{ fontSize: 13 }} /> : <Cancel sx={{ fontSize: 13 }} />}
                          label={lbl}
                          color={val ? 'success' : 'default'}
                          variant="outlined"
                          sx={{ height: 20 }}
                        />
                      ))}
                    </Stack>
                  </Box>
                )
              })}
            </Stack>
          )}

          <Divider sx={{ my: 1.5 }} />

          {/* Brand access / profile */}
          <SectionTitle icon={<Badge sx={{ fontSize: 18, color: '#0ea5e9' }} />}>
            Profile & brand access
          </SectionTitle>
          <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
            {(profile.clinicBrandIds || []).length === 0 ? (
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                No brand access on profile.
              </Typography>
            ) : (
              (profile.clinicBrandIds || []).map((id) => (
                <Chip key={id} size="small" label={brandNameById[id] || id} variant="outlined" sx={{ height: 22 }} />
              ))
            )}
          </Stack>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', sm: '1fr 1fr 1fr' }, gap: 1, mb: 0.5 }}>
            <KeyVal label="Profile name" value={profile.name} />
            <KeyVal label="Profile mobile" value={profile.mobile} />
            <KeyVal label="Language" value={profile.language} />
            <KeyVal label="Profile KYC" value={profile.kycStatus} />
          </Box>

          <Divider sx={{ my: 1.5 }} />

          {/* Assignment meta */}
          <SectionTitle icon={<Badge sx={{ fontSize: 18, color: '#64748b' }} />}>
            Assignment details
          </SectionTitle>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', sm: '1fr 1fr 1fr' }, gap: 1 }}>
            <KeyVal label="Assignment ID" value={assignment.id} />
            <KeyVal label="Clinic ID" value={assignment.clinicId} />
            <KeyVal label="Brand" value={brandNameById[assignment.clinicBrandId] || assignment.clinicBrandId} />
            <KeyVal label="KYC approved by" value={assignment.kycApprovedBy} />
            <KeyVal label="Start date" value={fmtDate(assignment.startDate)} />
            <KeyVal label="Approved" value={fmtDate(assignment.approvedTime)} />
            <KeyVal label="Created" value={fmtDate(assignment.createdTime)} />
            <KeyVal label="Modified" value={fmtDate(assignment.modifiedTime)} />
          </Box>

          <Divider sx={{ my: 1.5 }} />

          {/* Actions */}
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center">
            <Button
              size="small"
              variant={isDefault ? 'outlined' : 'contained'}
              color="warning"
              startIcon={isDefault ? <StarBorder /> : <Star />}
              disabled={lifecycleBusy}
              onClick={toggleDefault}
            >
              {isDefault ? 'Unset default' : 'Set as default'}
            </Button>
            {canManageLifecycle && (
              <>
                <Button
                  size="small"
                  variant="outlined"
                  color={isEnabled ? 'error' : 'success'}
                  disabled={lifecycleBusy}
                  onClick={toggleEnabled}
                >
                  {isEnabled ? 'Disable' : 'Enable'}
                </Button>
                {kyc !== 'approved' && (
                  <Button
                    size="small"
                    variant="contained"
                    color="success"
                    startIcon={<CheckCircle />}
                    disabled={lifecycleBusy}
                    onClick={() => doApproval('APPROVE')}
                  >
                    Approve
                  </Button>
                )}
                {kyc !== 'rejected' && (
                  <Button
                    size="small"
                    variant="text"
                    color="error"
                    startIcon={<Cancel />}
                    disabled={lifecycleBusy}
                    onClick={() => doApproval('REJECT')}
                  >
                    Reject
                  </Button>
                )}
              </>
            )}
            {lifecycleBusy && <CircularProgress size={18} />}
          </Stack>
        </AccordionDetails>
      </Accordion>

      <SupportRoleEditor
        open={editorOpen}
        supportRoles={supportRoles}
        availableManagers={availableManagers}
        onClose={() => setEditorOpen(false)}
        onSave={saveSupportRoles}
      />
    </Box>
  )
}
